import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'
import { PageHeader } from '@/components/ui/PageHeader'
import { LayoutList, ChevronUp, ChevronDown, ChevronsUpDown, Check, X } from 'lucide-react'
import { useAppData } from '@/contexts/AppDataContext'
import { useAuth } from '@/contexts/AuthContext'
import { SmartCombobox, invalidateDistinctCache } from '@/components/ui/SmartCombobox'
import {
    SEXE, BAILLEUR, ORGANISME_PAIEMENT, CAUSE_SUSP, ADMIN_TYPE, LIEU_VIE_TYPE
} from '@/config/enums'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Row = Record<string, any>

type EditType = 'text' | 'number' | 'date' | 'enum' | 'combobox' | 'worker'

interface EditDef {
    dbKey: string
    type: EditType
    options?: string[]
}

const FIELDS = [
    'id', 'prenom', 'nom', 'numero_suivi', 'sexe', 'date_naissance', 'registre_national',
    'organisme_paiement', 'lieu_vie_type', 'situation_residentielle', 'bailleur', 'administration_type',
    'historique_logement_1_date_signature_bail', 'historique_logement_1_date_renom',
    'historique_logement_2_date_signature_bail', 'historique_logement_2_date_renom',
    'nombre_logements_depuis_debut_suivi',
    'date_premiere_convention', 'date_derniere_convention',
    'referente', 'date_suspension_termination', 'cause_suspension_termination',
    'is_active',
]

const monthsSince = (dateStr: string | null | undefined): number | null => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return null
    const now = new Date()
    return Math.floor((now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()))
}

const computeAge = (dateStr: string | null | undefined): number | null => {
    if (!dateStr) return null
    const birth = new Date(dateStr)
    if (isNaN(birth.getTime())) return null
    const now = new Date()
    let age = now.getFullYear() - birth.getFullYear()
    const m = now.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--
    return age
}

const fmt = (s: string | null): string => {
    if (!s) return '—'
    try {
        return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    } catch { return s }
}

const adminAbbr = (v: string | null): string => {
    if (!v || v === 'Non') return '—'
    if (v === 'Administrateur de la personne') return 'ADP'
    if (v === 'Administrateur des biens') return 'ADB'
    if (v === 'Administrateur des biens et de la personne') return 'ADB+P'
    return v
}

const logementCell = (b: Row) => b.lieu_vie_type ?? b.situation_residentielle ?? '—'

type SortDir = 'asc' | 'desc'

interface ColDef {
    key: string
    header: string
    subHeader?: string
    render: (b: Row) => React.ReactNode
    sortKey?: string
    editDef?: EditDef
}

// Number fields that need numeric coercion on save
const NUMBER_DB_KEYS = new Set(['numero_suivi', 'nombre_logements_depuis_debut_suivi'])

const COLS: ColDef[] = [
    { key: 'suivi',  header: 'N° SUIVI',    render: b => b.numero_suivi ?? '—',  sortKey: 'numero_suivi',  editDef: { dbKey: 'numero_suivi', type: 'number' } },
    { key: 'prenom', header: 'PRÉNOM',      render: b => b.prenom ?? '—',         sortKey: 'prenom',        editDef: { dbKey: 'prenom', type: 'text' } },
    { key: 'nom',    header: 'NOM',         render: b => b.nom ?? '—',            sortKey: 'nom',           editDef: { dbKey: 'nom', type: 'text' } },
    { key: 'sexe',   header: 'SEXE',        render: b => b.sexe ?? '—',                                     editDef: { dbKey: 'sexe', type: 'enum', options: SEXE } },
    { key: 'naiss',  header: 'NAISSANCE',   render: b => fmt(b.date_naissance),                             editDef: { dbKey: 'date_naissance', type: 'date' } },
    { key: 'age',    header: 'ÂGE',         render: b => computeAge(b.date_naissance) ?? '—', sortKey: 'age' },
    { key: 'niss',   header: 'NISS',        render: b => b.registre_national ?? '—',                       editDef: { dbKey: 'registre_national', type: 'text' } },
    { key: 'revenu', header: 'REVENU',      render: b => b.organisme_paiement ?? '—', sortKey: 'organisme_paiement', editDef: { dbKey: 'organisme_paiement', type: 'combobox' } },
    { key: 'logt',   header: 'LOGEMENT',    render: b => logementCell(b),          sortKey: 'lieu_vie_type', editDef: { dbKey: 'lieu_vie_type', type: 'combobox' } },
    { key: 'baill',  header: 'BAILLEUR',    render: b => b.bailleur ?? '—',        sortKey: 'bailleur',     editDef: { dbKey: 'bailleur', type: 'combobox' } },
    { key: 'adm',    header: 'ADB / ADP',   render: b => adminAbbr(b.administration_type),                  editDef: { dbKey: 'administration_type', type: 'enum', options: ADMIN_TYPE } },
    { key: 'bail1',  header: 'BAIL 1',      render: b => fmt(b.historique_logement_1_date_signature_bail),  editDef: { dbKey: 'historique_logement_1_date_signature_bail', type: 'date' } },
    { key: 'renom1', header: 'RENOM 1',     render: b => fmt(b.historique_logement_1_date_renom),           editDef: { dbKey: 'historique_logement_1_date_renom', type: 'date' } },
    { key: 'bail2',  header: 'BAIL 2',      render: b => fmt(b.historique_logement_2_date_signature_bail),  editDef: { dbKey: 'historique_logement_2_date_signature_bail', type: 'date' } },
    { key: 'renom2', header: 'RENOM 2',     render: b => fmt(b.historique_logement_2_date_renom),           editDef: { dbKey: 'historique_logement_2_date_renom', type: 'date' } },
    { key: 'nblogt', header: 'NBRE', subHeader: 'Logements',  render: b => b.nombre_logements_depuis_debut_suivi ?? '—', sortKey: 'nombre_logements_depuis_debut_suivi', editDef: { dbKey: 'nombre_logements_depuis_debut_suivi', type: 'number' } },
    { key: 'conv1',  header: 'DATE', subHeader: '1re conv.',  render: b => fmt(b.date_premiere_convention),  editDef: { dbKey: 'date_premiere_convention', type: 'date' } },
    { key: 'conv2',  header: 'DATE', subHeader: 'Dern. conv.', render: b => {
        const m = monthsSince(b.date_derniere_convention)
        const color = m === null ? 'text-gray-700' : m >= 12 ? 'text-red-600 font-semibold' : m >= 9 ? 'text-orange-500 font-semibold' : 'text-green-700'
        return <span className={color}>{fmt(b.date_derniere_convention)}</span>
    }, editDef: { dbKey: 'date_derniere_convention', type: 'date' } },
    { key: 'mois',   header: 'MOIS', subHeader: 'conv.',      render: b => {
        const m = monthsSince(b.date_derniere_convention)
        if (m === null) return <span className="text-gray-400">—</span>
        const color = m >= 12 ? 'bg-red-100 text-red-700' : m >= 9 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
        return <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${color}`}>{m}</span>
    }},
    { key: 'ref',    header: 'RÉFÉRENT.E',  render: b => b.referente ?? '—', sortKey: 'referente', editDef: { dbKey: 'referente', type: 'worker' } },
    { key: 'finsui', header: 'DATE',   subHeader: 'Fin suivi',  render: b => fmt(b.date_suspension_termination), editDef: { dbKey: 'date_suspension_termination', type: 'date' } },
    { key: 'raison', header: 'RAISON', subHeader: 'Fin suivi',  render: b => b.cause_suspension_termination ?? '—', sortKey: 'cause_suspension_termination', editDef: { dbKey: 'cause_suspension_termination', type: 'combobox' } },
]

interface EditCell {
    rowId: string
    dbKey: string
    value: string
}

interface BeneficiairesOverviewProps {
    onNavigateBack: () => void
    onSelectBeneficiaire: (id: string) => void
}

export const BeneficiairesOverview = ({ onNavigateBack, onSelectBeneficiaire }: BeneficiairesOverviewProps) => {
    const { workers } = useAppData()
    const { userProfile } = useAuth()
    const isAdmin = userProfile?.is_admin === true
    const [rows, setRows] = useState<Row[]>([])
    const [loading, setLoading] = useState(true)
    const [showInactive, setShowInactive] = useState(false)
    const [sortCol, setSortCol] = useState<string>('suivi')
    const [sortDir, setSortDir] = useState<SortDir>('asc')
    const [editCell, setEditCell] = useState<EditCell | null>(null)
    const [saving, setSaving] = useState(false)
    const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null)

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            const { data } = await supabase
                .from('beneficiaries')
                .select(FIELDS.join(', '))
                .order('numero_suivi', { ascending: true, nullsFirst: false })
            if (data) setRows(data as Row[])
            setLoading(false)
        }
        load()
    }, [])

    const visible = rows.filter(b => showInactive || b.is_active !== false)

    const sorted = [...visible].sort((a, b) => {
        const col = COLS.find(c => c.key === sortCol)
        const key = col?.sortKey
        if (!key) return 0
        const av = key === 'age' ? (computeAge(a.date_naissance) ?? -1) : (a[key] ?? '')
        const bv = key === 'age' ? (computeAge(b.date_naissance) ?? -1) : (b[key] ?? '')
        const cmp = String(av).localeCompare(String(bv), 'fr', { numeric: true })
        return sortDir === 'asc' ? cmp : -cmp
    })

    // Enrich columns that need runtime data (worker names)
    const cols = COLS.map(col =>
        col.key === 'ref'
            ? {
                ...col,
                render: (b: Row) => workers.find(w => w.id === b.referente)?.name ?? '—',
            }
            : col
    )

    const handleSort = (colKey: string) => {
        if (editCell) return
        if (sortCol === colKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        else { setSortCol(colKey); setSortDir('asc') }
    }

    const startEdit = (b: Row, col: ColDef, e: React.MouseEvent) => {
        if (!col.editDef) return
        e.stopPropagation()
        setEditCell({ rowId: b.id, dbKey: col.editDef.dbKey, value: b[col.editDef.dbKey] ?? '' })
    }

    const commitEdit = async () => {
        if (!editCell || saving) return
        setSaving(true)
        const { rowId, dbKey, value } = editCell
        const coerced: any = value === ''
            ? null
            : NUMBER_DB_KEYS.has(dbKey)
                ? Number(value)
                : value
        await supabase.from('beneficiaries').update({ [dbKey]: coerced }).eq('id', rowId)
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, [dbKey]: coerced } : r))
        const col = COLS.find(c => c.editDef?.dbKey === dbKey)
        if (col?.editDef?.type === 'combobox') invalidateDistinctCache(dbKey)
        setEditCell(null)
        setSaving(false)
    }

    const cancelEdit = (e?: React.MouseEvent) => {
        e?.stopPropagation()
        setEditCell(null)
    }

    const renderEditWidget = (col: ColDef, b: Row) => {
        if (!col.editDef) return null
        const { type, options } = col.editDef
        const value = editCell!.value

        const baseClass = 'text-xs border border-blue-400 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[80px]'

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') commitEdit()
            if (e.key === 'Escape') cancelEdit()
        }

        return (
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                {type === 'combobox' && (
                    <SmartCombobox
                        column={col.editDef!.dbKey}
                        value={value}
                        onChange={v => setEditCell(prev => prev ? { ...prev, value: v } : null)}
                        isAdmin={isAdmin}
                        autoFocus
                        inputClassName={baseClass}
                        onKeyDown={handleKeyDown}
                    />
                )}
                {(type === 'enum') && (
                    <select
                        ref={el => { inputRef.current = el }}
                        value={value}
                        onChange={e => setEditCell(prev => prev ? { ...prev, value: e.target.value } : null)}
                        onKeyDown={handleKeyDown}
                        className={baseClass}
                        autoFocus
                    >
                        <option value="">—</option>
                        {options?.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                )}
                {type === 'worker' && (
                    <select
                        ref={el => { inputRef.current = el }}
                        value={value}
                        onChange={e => setEditCell(prev => prev ? { ...prev, value: e.target.value } : null)}
                        onKeyDown={handleKeyDown}
                        className={baseClass}
                        autoFocus
                    >
                        <option value="">—</option>
                        {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                )}
                {(type === 'text' || type === 'number' || type === 'date') && (
                    <input
                        ref={el => { inputRef.current = el }}
                        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
                        value={value}
                        onChange={e => setEditCell(prev => prev ? { ...prev, value: e.target.value } : null)}
                        onKeyDown={handleKeyDown}
                        className={`${baseClass} ${type === 'text' ? 'w-24' : ''}`}
                        autoFocus
                    />
                )}
                <button
                    type="button"
                    onClick={commitEdit}
                    disabled={saving}
                    className="p-0.5 text-green-600 hover:text-green-700 disabled:opacity-50"
                    title="Enregistrer"
                >
                    <Check size={12} />
                </button>
                <button
                    type="button"
                    onClick={cancelEdit}
                    className="p-0.5 text-red-400 hover:text-red-500"
                    title="Annuler"
                >
                    <X size={12} />
                </button>
            </div>
        )
    }

    const SortIcon = ({ colKey }: { colKey: string }) => {
        if (sortCol !== colKey) return <ChevronsUpDown className="inline ml-1 h-3 w-3 opacity-30" />
        return sortDir === 'asc'
            ? <ChevronUp className="inline ml-1 h-3 w-3 text-blue-500" />
            : <ChevronDown className="inline ml-1 h-3 w-3 text-blue-500" />
    }

    const thClass = (col: ColDef) => [
        'px-2 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wide border-r border-gray-200 last:border-r-0 whitespace-nowrap',
        col.sortKey ? 'cursor-pointer hover:bg-gray-200 select-none' : '',
    ].join(' ')

    const tdClass = (col: ColDef, even: boolean, isEditing: boolean) => [
        'px-2 py-1 text-xs text-gray-700 border-r border-gray-100 last:border-r-0 whitespace-nowrap',
        col.editDef && !IS_DEMO_MODE && !isEditing ? 'cursor-pointer hover:bg-blue-50 group' : '',
        isEditing ? 'bg-blue-50' : even ? 'bg-white' : 'bg-gray-50/80',
    ].join(' ')

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader
                title="Vue d'ensemble"
                icon={<LayoutList className="h-5 w-5" />}
                onBack={onNavigateBack}
            />

            <div className="sm:hidden bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-xs text-yellow-800">
                Vue optimisée pour ordinateur — faites défiler horizontalement.
            </div>

            <div className="px-4 py-4">
                <div className="flex items-center gap-4 mb-3">
                    <p className="text-sm text-gray-500">
                        {sorted.length} locataire{sorted.length !== 1 ? 's' : ''}
                    </p>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="rounded" />
                        Afficher inactifs
                    </label>
                    <p className="text-xs text-gray-400">Cliquer sur une cellule pour l'éditer · Cliquer sur # pour ouvrir la fiche</p>
                </div>

                {loading ? (
                    <div className="flex justify-center pt-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                        <table className="border-collapse text-xs">
                            <thead>
                                <tr className="bg-gray-100 sticky top-0 z-30">
                                    <th className="sticky left-0 z-30 w-8 px-2 py-2 text-left text-[10px] font-bold text-gray-500 bg-gray-100 border-r border-gray-200 select-none">
                                        #
                                    </th>
                                    {cols.map(col => (
                                        <th
                                            key={col.key}
                                            onClick={col.sortKey ? () => handleSort(col.key) : undefined}
                                            className={thClass(col)}
                                        >
                                            <span className="leading-tight">
                                                {col.header}
                                                {col.subHeader && <><br /><span className="font-normal normal-case text-gray-500">{col.subHeader}</span></>}
                                            </span>
                                            {col.sortKey && <SortIcon colKey={col.key} />}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((b, i) => {
                                    const even = i % 2 === 0
                                    return (
                                        <tr
                                            key={b.id}
                                            className={`border-b border-gray-100 last:border-b-0 ${b.is_active === false ? 'opacity-40' : ''}`}
                                        >
                                            {/* # cell — click to open detail */}
                                            <td
                                                onClick={() => onSelectBeneficiaire(b.id)}
                                                className={`sticky left-0 z-10 w-8 px-2 py-1.5 text-gray-400 font-mono text-[10px] border-r border-gray-200 cursor-pointer hover:text-blue-500 hover:bg-blue-50 ${even ? 'bg-white' : 'bg-gray-50/80'}`}
                                            >
                                                {i + 1}
                                            </td>
                                            {cols.map(col => {
                                                const isEditing = editCell?.rowId === b.id && editCell?.dbKey === col.editDef?.dbKey
                                                return (
                                                    <td
                                                        key={col.key}
                                                        className={tdClass(col, even, isEditing)}
                                                        onClick={!IS_DEMO_MODE && !isEditing && col.editDef ? (e) => startEdit(b, col, e) : undefined}
                                                    >
                                                        {isEditing
                                                            ? renderEditWidget(col, b)
                                                            : col.render(b)
                                                        }
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })}
                                {sorted.length === 0 && (
                                    <tr>
                                        <td colSpan={cols.length + 1} className="px-4 py-12 text-center text-gray-400 text-sm">
                                            Aucun locataire trouvé
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}
